import React, { useCallback, useMemo, useRef, useState } from "react";

// Translations
import { useTranslation } from "react-i18next";

// Material UI
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import FormLabel from "@mui/material/FormLabel";
import AntSwitch from "../AntSwitch/index";
import TextField from "@mui/material/TextField";
import ButtonGroup from "@mui/material/ButtonGroup";
import InputAdornment from "@mui/material/InputAdornment";
import FormHelperText from "@mui/material/FormHelperText";
import Typography from "@mui/material/Typography";
// Icons
import { HiOutlineSparkles } from "react-icons/hi";
import { SlBulb } from "react-icons/sl";

// Icons
import { ReactComponent as CloseIcon } from 'images/close-stroke.svg';
import { ReactComponent as LeftArrowIcon } from 'images/left-arrow-stroke.svg';

// Styles
import styles from "./style.module.scss";

// Types
import { QuizSetupData, TimeLimitOption, QuestionType, Note, APIId } from "../../types";
import { Link } from "../AttachMaterials/types";

// Components
import NoteGroup from "../AttachMaterials/NoteGroup";
import LanguageSelect from "../LanguageSelect";
import AttachMaterials from "../AttachMaterials";
import HelpTooltip from "../HelpTooltip";

// Hooks
import useShortCutKey from "../../hooks/useShortCutKey";
import { useMobile } from "../../utils/hooks/isMobile";

interface QuizSetupModalProps {
  allowFileUpload: boolean;
  open: boolean;
  onClose: () => void;
  onGenerate: (data: QuizSetupData) => void;
  languages?: Record<string, string>;
  showModalTabs?: boolean;
}

const QuizSetupModal: React.FC<QuizSetupModalProps> = ({ allowFileUpload, open, onClose, onGenerate, languages, showModalTabs = true }) => {
  const { t } = useTranslation();
  const isMobile = useMobile();

  const [formView, setFormView] = useState<'form' | 'file_upload'>('form');
  const [error, setError] = useState<string | null>(null);
  const [timeLimit, setTimeLimit] = useState<TimeLimitOption>('no_limit');
  const [customTimeLimit, setCustomTimeLimit] = useState<string>('');
  const [questions, setQuestions] = useState<QuestionType[]>([]);
  const [adaptiveMix, setAdaptiveMix] = useState<boolean>(false);
  const [skippingHints, setSkippingHints] = useState<boolean>(false);
  const [notes, setNotes] = useState<Note[]>([]);
  const [files, setFiles] = useState<string[]>([]);
  const [links, setLinks] = useState<string[]>([]);
  const [prompt, setPrompt] = useState<string>('');
  const [selectedQuestionTypes, setSelectedQuestionTypes] = useState<{ [key in QuestionType]?: boolean }>({});
  const [questionQuantities, setQuestionQuantities] = useState<{ [key in QuestionType]?: number }>({});
  const [maxQuestions, setMaxQuestions] = useState<number>(50);
  const [selectedLanguage, setSelectedLanguage] = useState<string>('en');
  const [settingsCompleted, setSettingsCompleted] = useState<boolean>(false);

  // Refs
  const formRef = useRef<HTMLDivElement>(null);

  // Hooks

  const timeLimitOptions = useMemo(() => {
    return [
      {
        value: 'no_limit',
        label: t('quizSetupModal.time_limit_options.no_limit'),
        reduced: t('quizSetupModal.time_limit_options.no_limit_reduced')
      },
      {
        value: '5',
        label: t('quizSetupModal.time_limit_options.5_minutes'),
        reduced: t('quizSetupModal.time_limit_options.5_minutes_reduced')
      },
      {
        value: '10',
        label: t('quizSetupModal.time_limit_options.10_minutes'),
        reduced: t('quizSetupModal.time_limit_options.10_minutes_reduced')
      },
      {
        value: '20',
        label: t('quizSetupModal.time_limit_options.20_minutes'),
        reduced: t('quizSetupModal.time_limit_options.20_minutes_reduced')
      },
      {
        value: 'custom',
        label: t('quizSetupModal.time_limit_options.custom'),
        reduced: t('quizSetupModal.time_limit_options.custom_reduced')
      },
    ]
  }, [t]);

  const handleQuestionTypeChange = useCallback((type: QuestionType, checked: boolean) => {
    setSelectedQuestionTypes(prev => {
      const newSelected = { ...prev, [type]: checked };

      if (!checked) {
        setQuestionQuantities(prevQty => {
          const newQuantities = { ...prevQty };
          delete newQuantities[type];
          return newQuantities;
        });
      } else {
        const currentTotal = Object.values(questionQuantities).reduce((sum, qty) => sum + (qty || 0), 0);
        if (currentTotal >= maxQuestions) {
          return prev;
        }
      }

      return newSelected;
    });
  }, [questionQuantities, maxQuestions]);

  const selectedTypesCount = useMemo(() => {
    return Object.values(selectedQuestionTypes).filter(Boolean).length;
  }, [selectedQuestionTypes]);

  const totalSelectedQuantity = useMemo(() => {
    return Object.values(questionQuantities).reduce((sum, qty) => sum + (qty || 0), 0);
  }, [questionQuantities]);

  const questionTypes = useMemo(() => {
    const isAtMaxCapacity = totalSelectedQuantity >= maxQuestions;

    return [
      // TODO: Hide multiple choice question type for now
      // {
      //   value: 'multiple_choice' as QuestionType,
      //   label: t('quizSetupModal.question_type.multiple_choice'),
      //   checked: selectedQuestionTypes.multiple_choice || false,
      //   onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
      //     if (!(isAtMaxCapacity && !selectedQuestionTypes.multiple_choice)) {
      //       handleQuestionTypeChange('multiple_choice', e.target.checked);
      //     }
      //   },
      //   disabled: isAtMaxCapacity && !selectedQuestionTypes.multiple_choice
      // },
      {
        value: 'single_answer' as QuestionType,
        label: t('quizSetupModal.question_type.multiple_choice'),
        checked: selectedQuestionTypes.single_answer || false,
        onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
          if (!(isAtMaxCapacity && !selectedQuestionTypes.single_answer)) {
            handleQuestionTypeChange('single_answer', e.target.checked);
          }
        },
        disabled: isAtMaxCapacity && !selectedQuestionTypes.single_answer
      },
      {
        value: 'matching_pairs' as QuestionType,
        label: t('quizSetupModal.question_type.matching_pairs'),
        checked: selectedQuestionTypes.matching_pairs || false,
        onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
          if (!(isAtMaxCapacity && !selectedQuestionTypes.matching_pairs)) {
            handleQuestionTypeChange('matching_pairs', e.target.checked);
          }
        },
        disabled: isAtMaxCapacity && !selectedQuestionTypes.matching_pairs
      },
      {
        value: 'true_false' as QuestionType,
        label: t('quizSetupModal.question_type.true_false'),
        checked: selectedQuestionTypes.true_false || false,
        onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
          if (!(isAtMaxCapacity && !selectedQuestionTypes.true_false)) {
            handleQuestionTypeChange('true_false', e.target.checked);
          }
        },
        disabled: isAtMaxCapacity && !selectedQuestionTypes.true_false
      },
      {
        value: 'fill_in_the_blank' as QuestionType,
        label: t('quizSetupModal.question_type.fill_in_the_blank'),
        checked: selectedQuestionTypes.fill_in_the_blank || false,
        onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
          if (!(isAtMaxCapacity && !selectedQuestionTypes.fill_in_the_blank)) {
            handleQuestionTypeChange('fill_in_the_blank', e.target.checked);
          }
        },
        disabled: isAtMaxCapacity && !selectedQuestionTypes.fill_in_the_blank
      },
    ]
  }, [t, selectedQuestionTypes, handleQuestionTypeChange, totalSelectedQuantity, maxQuestions]);

  const handleQuantityChange = useCallback((type: QuestionType, value: string) => {
    const numValue = parseInt(value) || 0;

    const otherTypesTotal = Object.entries(questionQuantities)
      .filter(([otherType]) => otherType !== type)
      .reduce((sum, [, qty]) => sum + (qty || 0), 0);

    const newTotal = otherTypesTotal + numValue;

    if (newTotal > maxQuestions) {
      const maxAllowedForThisType = maxQuestions - otherTypesTotal;
      const cappedValue = Math.max(0, maxAllowedForThisType);

      setQuestionQuantities(prev => ({
        ...prev,
        [type]: cappedValue
      }));
    } else {
      setQuestionQuantities(prev => ({
        ...prev,
        [type]: numValue
      }));
    }

    if (newTotal >= maxQuestions) {
      setSelectedQuestionTypes(prev => {
        const newSelected = { ...prev };

        Object.keys(newSelected).forEach(questionType => {
          const typeKey = questionType as QuestionType;
          if (typeKey !== type && (!questionQuantities[typeKey] || questionQuantities[typeKey] === 0)) {
            newSelected[typeKey] = false;
          }
        });

        return newSelected;
      });
    }
  }, [maxQuestions, questionQuantities]);

  const isQuantityValid = useMemo(() => {
    if (selectedTypesCount <= 1) return true;
    return totalSelectedQuantity <= maxQuestions;
  }, [selectedTypesCount, totalSelectedQuantity, maxQuestions]);

  const hasPrompt = useMemo(() => {
    return prompt && prompt.trim().length > 0;
  }, [prompt]);

  const hasMaterials = useMemo(() => {
    return notes.length > 0 || files.length > 0 || links.length > 0;
  }, [notes, files, links]);

  const isNextButtonDisabled = useMemo(() => {
    if (formView === 'form') {
      return selectedTypesCount === 0 || totalSelectedQuantity === 0;
    } else if (formView === 'file_upload') {
      return !hasMaterials && !prompt?.trim();
    }
    return false;
  }, [selectedTypesCount, totalSelectedQuantity, formView, hasMaterials, prompt]);

  const getMaxAllowedForType = useCallback((currentType: QuestionType) => {
    const otherTypesTotal = Object.entries(questionQuantities)
      .filter(([type]) => type !== currentType)
      .reduce((sum, [, qty]) => sum + (qty || 0), 0);
    return maxQuestions - otherTypesTotal;
  }, [questionQuantities, maxQuestions]);

  const handleStartGeneration = useCallback(() => {
    if (allowFileUpload && formView === 'form') {
      setSettingsCompleted(true);
      setFormView('file_upload');
      return;
    }

    if (formView === 'file_upload' && !hasMaterials && !prompt?.trim()) {
      setError(t('quizSetupModal.error.no_note_selected'));
      return;
    }

    const selectedTypes = Object.entries(selectedQuestionTypes)
      .filter(([, checked]) => checked)
      .map(([type]) => type as QuestionType);

    const questionsArray = selectedTypes.map(type => ({
      question_type: type,
      qty: questionQuantities[type] || Math.floor(maxQuestions / selectedTypesCount)
    }));

    const data: QuizSetupData = {
      timeLimit: timeLimit === 'custom' ? Number(customTimeLimit) * 60 : timeLimit === 'no_limit' ? null : Number(timeLimit) * 60,
      questions: questionsArray,
      questionQuantities: questionQuantities,
      adaptiveMix,
      skippingHints,
      prompt,
      note: notes.length > 0 ? notes[0] : null,
      files,
      links,
      max_questions: totalSelectedQuantity,
      language: selectedLanguage,
    };

    onGenerate(data);
  }, [onGenerate, timeLimit, adaptiveMix, skippingHints, formView, allowFileUpload, customTimeLimit, prompt, notes, files, links, maxQuestions, selectedQuestionTypes, selectedTypesCount, isQuantityValid, totalSelectedQuantity, questionQuantities, selectedLanguage, hasMaterials]);
  // Shortcut key to start generation
  useShortCutKey({ key: 'Enter', onKeyPress: handleStartGeneration, active: open });

  const handleCustomTimeLimitChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (!/^\d+$/.test(value)) {
      setCustomTimeLimit("");
    } else {
      let num = Math.max(0, Math.min(1000, Number(value)));
      setCustomTimeLimit(num.toString());
    }
  };


  const handleSelectSmartNote = useCallback((selectedNotes: Note[]) => {
    setError(null);
    setNotes(selectedNotes);
    if (selectedNotes.length > 0 && prompt.trim()) {
      setPrompt('');
    }
  }, [prompt]);

  const handleChangeUploadedFiles = useCallback((uploadedFiles: string[]) => {
    setError(null);
    setFiles(uploadedFiles);
    if (uploadedFiles.length > 0 && prompt.trim()) {
      setPrompt('');
    }
  }, [prompt]);

  const handleLinksChange = useCallback((changedLinks: Link[]) => {
    const linkUrls = changedLinks.map(link => link.url).filter(url => url.trim() !== '');
    setError(null);
    setLinks(linkUrls);
    if (linkUrls.length > 0 && prompt.trim()) {
      setPrompt('');
    }
  }, [prompt]);

  const handleRemoveNote = useCallback((noteId: APIId) => {
    setNotes(prev => prev.filter(n => n.id !== noteId));
  }, []);

  const handlePromptChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPrompt(value);
    if (value.trim() && (notes.length > 0 || files.length > 0 || links.length > 0)) {
      setNotes([]);
      setFiles([]);
      setLinks([]);
    }
  }, [notes, files, links]);

  const handleTabClick = useCallback((tab: 'form' | 'file_upload') => {
    if (tab === 'file_upload' && !settingsCompleted) {
      return;
    }

    if (tab === 'file_upload' && (selectedTypesCount === 0 || totalSelectedQuantity === 0)) {
      return;
    }

    setFormView(tab);
  }, [settingsCompleted, selectedTypesCount, totalSelectedQuantity]);

  const handleClose = () => {
    // Reset the form
    setFormView('form');
    setError(null);
    setSettingsCompleted(false);
    setNotes([]);
    setFiles([]);
    setLinks([]);
    setPrompt('');
    setTimeLimit('no_limit');
    setCustomTimeLimit('');
    setAdaptiveMix(false);
    setSkippingHints(false);
    setSelectedQuestionTypes({});
    setQuestionQuantities({});
    setMaxQuestions(50);
    setSelectedLanguage('en');

    onClose();
  };

  return (
    <Dialog color="primary" open={open} onClose={handleClose} ref={formRef} className={styles.dialog}>
      <DialogTitle className={`${styles.header} dark:bg-zeus-700`}>
        <div className="w-8 h-8">
          {formView !== 'form' && (
            <button
              className="flex justify-center items-center rounded-full bg-stone-50 hover:opacity-80 dark:hover:bg-zeus-900 h-8 w-8 relative z-10"
              onClick={() => handleTabClick('form')}
            >
              <LeftArrowIcon className="w-[10px] h-[10px] stroke-3" />
            </button>
          )}
        </div>

        <div className='flex items-center gap-2'>
          <span>{t('quizSetupModal.title')}</span>
          <HelpTooltip type="quiz" />
        </div>

        <button
          className="flex justify-center items-center rounded-full bg-stone-50 hover:opacity-80 dark:hover:bg-zeus-900 h-8 w-8 relative z-10"
          onClick={handleClose}
        >
          <CloseIcon className="w-[10px] h-[10px] fill-stone-700 dark:stroke-white stroke-3" />
        </button>
      </DialogTitle>

      {showModalTabs && (
        <div className={`${styles.tab_lines} dark:bg-zeus-700`}>
          <div
            className={`${styles.tab_line} ${formView === 'form' ? styles.active : ''} ${settingsCompleted ? styles.active : ''}`}
            onClick={() => handleTabClick('form')}
          />
          <div
            className={`${styles.tab_line} ${formView === 'file_upload' ? styles.active : ''} ${settingsCompleted ? styles.active : ''} ${!settingsCompleted || selectedTypesCount === 0 || totalSelectedQuantity === 0 ? styles.disabled : ''}`}
            onClick={() => handleTabClick('file_upload')}
          />
        </div>
      )}

      <DialogContent className={`${styles.content} ${showModalTabs ? styles.contentWithTabs : ''} dark:bg-zeus-700`}>
        {formView === 'form' && (
          <>
            <div className={styles.format_of_questions}>
              <FormLabel>
                {t('quizSetupModal.format_of_questions')}
              </FormLabel>

              <div className={`${styles.questions_container} dark:bg-stone-650`}>
                <div className={`${styles.questions_sub_container} dark:bg-stone-650`}>
                  {questionTypes.map((type) => (
                    <div key={type.value} className={styles.question_type_row}>
                      <div className={styles.question_type_row_content}>
                        <FormLabel
                          htmlFor={`toggle-${type.value}`}
                          className={type.checked ? styles.label_checked : ''}
                        >
                          {type.label}
                        </FormLabel>

                        {type.checked && !isMobile && (
                          <TextField
                            type="number"
                            size="small"
                            placeholder="0"
                            value={questionQuantities[type.value] || ''}
                            onChange={(e) => handleQuantityChange(type.value, e.target.value)}
                            className={styles.quantity_input_small}
                            slotProps={{
                              input: {
                                inputProps: {
                                  min: 0,
                                  max: getMaxAllowedForType(type.value),
                                  step: 1
                                }
                              }
                            }}
                          />
                        )}
                        <AntSwitch
                          id={`toggle-${type.value}`}
                          checked={type.checked}
                          onChange={type.onChange}
                          disabled={type.disabled}
                        />
                      </div>

                      {isMobile && type.checked && (
                        <TextField
                          type="number"
                          size="small"
                          placeholder="0"
                          value={questionQuantities[type.value] || ''}
                          onChange={(e) => handleQuantityChange(type.value, e.target.value)}
                          className={styles.quantity_input_small}
                          slotProps={{
                            input: {
                              inputProps: {
                                min: 0,
                                max: getMaxAllowedForType(type.value),
                                step: 1
                              }
                            }
                          }}
                        />
                      )}
                    </div>
                  ))}
                </div>
                <div className={styles.quantity_summary}>
                  <Typography variant="body2" className={`${styles.quantity_summary} dark:text-white`}>
                    {t('quizSetupModal.total_questions')} <span style={{ color: '#008CFF' }}>{totalSelectedQuantity}</span> / {maxQuestions}
                  </Typography>
                </div>
              </div>
            </div>

            <div className={styles.format_of_questions}>
              <FormLabel>
                {t('quizSetupModal.time_limit')}
              </FormLabel>

              <ButtonGroup className={styles.time_limit_options}>
                {timeLimitOptions.map((option) =>
                  <Button
                    key={option.value}
                    variant={timeLimit === option.value ? 'contained' : 'outlined'}
                    role='button'
                    color="primary"
                    onClick={() => setTimeLimit(option.value as TimeLimitOption)}
                    className={styles.time_limit_option}
                  >
                    {isMobile ? option.reduced : option.label}
                  </Button>
                )}
              </ButtonGroup>

              {timeLimit === 'custom' && (
                <TextField
                  placeholder={t('quizSetupModal.custom_placeholder')}
                  value={customTimeLimit}
                  type="number"
                  inputProps={{ min: 1, max: 999 }}
                  slotProps={{
                    input: {
                      endAdornment: (
                        <InputAdornment position="end">
                          {t('globals.mins')}
                        </InputAdornment>
                      )
                    }
                  }}
                  onChange={handleCustomTimeLimitChange}
                />
              )}
            </div>

            {languages && (
              <div className="flex flex-col gap-3">
                <FormLabel htmlFor="language-select" className="cursor-pointer">
                  {t('quizSetupModal.language')}
                </FormLabel>
                <LanguageSelect value={selectedLanguage} onChange={setSelectedLanguage} languages={languages} />
              </div>
            )}

            <div className={styles.adaptive_mix}>
              <div className={styles.adaptive_mix_header}>
                <div className={styles.adaptive_mix_header_label}>
                  <HiOutlineSparkles className={styles.adaptive_mix_header_label_icon} />

                  <FormLabel>
                    {t('quizSetupModal.adaptive_mix')}
                  </FormLabel>
                </div>

                <AntSwitch
                  checked={adaptiveMix}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAdaptiveMix(e.target.checked)}
                />
              </div>

              <FormHelperText className={styles.adaptive_mix_description}>
                {t('quizSetupModal.adaptive_mix_description')}
              </FormHelperText>
            </div>

            <div className={styles.adaptive_mix_header}>
              <div className={styles.adaptive_mix_header_label}>
                <SlBulb className={styles.adaptive_mix_header_label_icon} />

                <FormLabel>
                  {t('quizSetupModal.skipping_hints')}
                </FormLabel>
              </div>

              <AntSwitch
                checked={skippingHints}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSkippingHints(e.target.checked)}
              />
            </div>
          </>
        )}

        {formView === 'file_upload' && (
          <div>
            {!hasPrompt && (
              <AttachMaterials
                onChangeNotes={handleSelectSmartNote}
                onChangeUploadedFiles={handleChangeUploadedFiles}
                onChangeLinks={handleLinksChange}
              />
            )}

            {!hasPrompt && !hasMaterials && (
              <div className="flex items-center justify-center my-4 text-gray-400">
                <Typography variant="body1">{t('flashcardSetupModal.step_2.or')}</Typography>
              </div>
            )}

            {(!hasMaterials || hasPrompt) && (
              <div className={styles.format_of_questions}>
                <FormLabel>
                  {t('flashcardSetupModal.step_2.prompt')}
                </FormLabel>

                <TextField
                  placeholder={t('flashcardSetupModal.step_2.prompt_placeholder')}
                  value={prompt}
                  onChange={handlePromptChange}
                  className={styles.prompt_input}
                />
              </div>
            )}
          </div>
        )}
      </DialogContent>

      <DialogActions className={`${styles.actions} dark:bg-zeus-700`}>
        <Button
          variant="contained"
          fullWidth
          color="primary"
          role='button'
          onClick={handleStartGeneration}
          disabled={isNextButtonDisabled}
          className={`${styles.start_generation_button} dark:bg-zeus-800 dark:text-white`}
        >
          {allowFileUpload && formView === 'form' ? t('actions.next') : t('quizSetupModal.start_generation')}
        </Button>
      </DialogActions>
    </Dialog >
  );
};

export default QuizSetupModal;
